import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type DisplayProfile } from "../api";
import { useAuth } from "../auth";
import { errorText, useLoad } from "../hooks";
import { DEFAULT_ACUITY, type Eye, type Protocol } from "../lib/protocol";
import { probe } from "../lib/probe";
import { Geometry } from "../lib/units";
import { experimentSummary, programSummary } from "../lib/summaries";

export function SessionSetup() {
  const patientId = Number(useParams().id);
  const { user } = useAuth();
  const nav = useNavigate();
  const stations = useLoad(() => api.stations(), []);
  const programs = useLoad(() => api.programs(), []);
  const patient = useLoad(() => api.patient(patientId), [patientId]);
  const [programId, setProgramId] = useState<number | null>(null);
  const [stationId, setStationId] = useState<number | null>(null);
  const [displays, setDisplays] = useState<DisplayProfile[]>([]);
  const [displayId, setDisplayId] = useState<number | null>(null);
  const [distanceM, setDistanceM] = useState(user?.role === "patient" ? 1 : 4);
  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isPatient = user?.role === "patient";

  const choose = (id: number) => {
    const prog = programs.data?.find((x) => x.id === id);
    if (!prog) return;
    setProgramId(prog.id);
    setProtocol(structuredClone(prog.protocol));
    setDistanceM(prog.protocol.viewing_distance_mm / 1000);
  };
  useEffect(() => {
    if (!programs.data || !patient.data || programId !== null) return;
    const mine = programs.data.find((x) => x.id === patient.data!.program_id)
      ?? programs.data.find((x) => x.is_default) ?? programs.data[0];
    if (mine) choose(mine.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs.data, patient.data]);
  useEffect(() => {
    if (stationId === null) return;
    api.displays(stationId).then((d) => { setDisplays(d); setDisplayId(d[0]?.id ?? null); }, (e) => setError(errorText(e)));
  }, [stationId]);
  useEffect(() => {
    if (stations.data?.length && stationId === null) setStationId(stations.data[0]!.id);
  }, [stations.data, stationId]);

  const display = displays.find((d) => d.id === displayId) ?? null;
  const acuity = protocol?.steps.find((st) => st.experiment.kind === "acuity")?.experiment.acuity ?? DEFAULT_ACUITY;
  const geometry = display ? new Geometry(distanceM * 1000, display.width_mm, display.width_px, display.height_px) : null;

  const toggleEye = (eye: Eye) => {
    if (!protocol) return;
    const eyes = protocol.eyes.includes(eye) ? protocol.eyes.filter((e) => e !== eye) : [...protocol.eyes, eye];
    const order: Eye[] = ["OD", "OS", "OU"];
    setProtocol({ ...protocol, eyes: order.filter((e) => eyes.includes(e)) });
  };

  const create = async () => {
    if (!protocol) return;
    setError(null);
    try {
      const client_info = await probe();
      const s = await api.createSession({
        patient_id: patientId, mode: isPatient ? "home" : "clinic", display_profile_id: displayId,
        station_id: stationId, viewing_distance_mm: Math.round(distanceM * 1000), protocol, client_info,
        program_id: programId,
      });
      nav(`/sessions/${s.id}/run`);
    } catch (e) {
      setError(errorText(e));
    }
  };

  if (!protocol) return <div>Loading…</div>;
  return (
    <>
      <h1>New session</h1>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Station and distance</h2>
        {stations.data?.length === 0 && <p className="warn">No stations yet. <Link to="/stations">Register this screen first.</Link></p>}
        <div className="row">
          <label>Station
            <select aria-label="Station" value={stationId ?? ""} onChange={(e) => setStationId(Number(e.target.value))}>
              {(stations.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.kind})</option>)}
            </select>
          </label>
          <label>Display
            <select aria-label="Display" value={displayId ?? ""} onChange={(e) => setDisplayId(Number(e.target.value))}>
              {displays.map((d) => <option key={d.id} value={d.id}>{d.label || `display ${d.id}`} — {d.width_px}×{d.height_px}</option>)}
            </select>
          </label>
          <label>Viewing distance (m)
            <input type="number" step="0.01" min="0.3" max="8" value={distanceM} onChange={(e) => setDistanceM(Number(e.target.value))} />
          </label>
        </div>
        {geometry && (
          <p className="small muted">
            {geometry.pxPerDeg.toFixed(0)} px/degree. Letters from logMAR {Math.max(acuity.min_logmar, geometry.minResolvableLogmar(1)).toFixed(2)} to {Math.min(acuity.max_logmar, geometry.maxLogmar()).toFixed(2)} can be drawn;
            below logMAR {geometry.minResolvableLogmar(3).toFixed(2)} strokes are under 3 px, so 0.1 logMAR steps start to merge.
            {geometry.maxLogmar() < 1.0 && <span className="warn"> Low-vision acuity needs a shorter distance.</span>}
          </p>
        )}
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Program</h2>
        <div className="row">
          <label>Program
            <select aria-label="Program" value={programId ?? ""} onChange={(e) => choose(Number(e.target.value))}>
              {(programs.data ?? []).filter((x) => x.active).map((x) => (
                <option key={x.id} value={x.id}>{x.name}{x.id === patient.data?.program_id ? " (patient's)" : x.is_default ? " (default)" : ""}</option>
              ))}
            </select>
          </label>
        </div>
        <p className="small muted">{programSummary(protocol)}. Changes below apply to this session only; to change an
          experiment's settings, edit it (or a copy) on the Experiments page.</p>
        {!isPatient && (
          <table className="req-table">
            <thead><tr><th>Run</th><th>Experiment</th><th>Times</th><th>What it does</th></tr></thead>
            <tbody>{protocol.steps.map((st, i) => (
              <tr key={i}>
                <td><input type="checkbox" aria-label={`Run ${st.name}`} checked={st.times > 0}
                  disabled={protocol.steps.length === 1}
                  onChange={(e) => setProtocol({ ...protocol, steps: e.target.checked ? protocol.steps
                    : protocol.steps.filter((_, k) => k !== i) })} /></td>
                <td>{st.name}</td>
                <td><input type="number" min={1} max={10} style={{ width: "4rem" }} aria-label={`${st.name} times`} value={st.times}
                  onChange={(e) => setProtocol({ ...protocol, steps: protocol.steps.map((x, k) => (k === i
                    ? { ...x, times: Math.min(10, Math.max(1, Math.round(Number(e.target.value) || 1))) } : x)) })} /></td>
                <td className="small">{experimentSummary(st.experiment)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        <div className="row">
          <fieldset><legend>Eyes, in order</legend>
            {(["OD", "OS", "OU"] as Eye[]).map((e) => (
              <label key={e} style={{ flexDirection: "row", gap: "0.3rem" }}>
                <input type="checkbox" checked={protocol.eyes.includes(e)} onChange={() => toggleEye(e)} />{e}
              </label>
            ))}
          </fieldset>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      <button disabled={!displayId || protocol.eyes.length === 0} onClick={() => void create()}>Create session</button>
    </>
  );
}
